import { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { AccordionSection, Badge, IconHighlight, SearchInput } from '@aimsos/ui';

import { integrationLabelKey } from '../constants/integrations.js';
import { useDatasets } from '../hooks/useDatasets.js';
import PickerOption from './PickerOption.jsx';
import PickerOptionGroup from './PickerOptionGroup.jsx';
import { PickerError, PickerSkeleton } from './PickerStates.jsx';

import { Table2 } from 'lucide-react';

/** Dataset shape → DS badge variant. */
const SHAPE_VARIANT = {
  grouped: 'informative',
  single_value: 'purple',
  record_set: 'secondary',
};

/**
 * One dataset card (CT-5191). Same anatomy as `EntityPickerCard`: name,
 * description, and a badge row — here the shape badge plus the integration.
 *
 * `memo()`: these render in a grid alongside the entity list, and the parent
 * passes one stable `onSelect`.
 */
const DatasetPickerCard = memo(function DatasetPickerCard({
  dataset,
  shapeLabel,
  integrationLabel,
  selected,
  onSelect,
}) {
  return (
    <PickerOption
      value={dataset.id}
      selected={selected}
      onSelect={onSelect}
      ariaLabel={`${dataset.name} · ${shapeLabel} · ${integrationLabel}`}
      className="p-3"
    >
      {/* `aria-hidden`: `PickerOption` is already the radio, named by
          `ariaLabel`, so none of this should be announced twice. */}
      <div className="flex min-w-0 items-start gap-2.5" aria-hidden="true">
        <IconHighlight icon={Table2} bgColor="Primary" iconColor="PrimaryAlt" size="small" />

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="truncate text-sm font-medium text-content">{dataset.name}</span>

          {dataset.description ? (
            <p className="m-0 line-clamp-2 text-xs text-content-muted">{dataset.description}</p>
          ) : null}

          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <Badge size="small" variant={SHAPE_VARIANT[dataset.shape] ?? 'neutral'}>
              {shapeLabel}
            </Badge>
            {integrationLabel ? (
              <Badge size="small" variant="secondary">{integrationLabel}</Badge>
            ) : null}
          </div>
        </div>
      </div>
    </PickerOption>
  );
});

DatasetPickerCard.propTypes = {
  dataset: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    shape: PropTypes.string,
  }).isRequired,
  shapeLabel: PropTypes.string.isRequired,
  integrationLabel: PropTypes.string,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

/**
 * `GOVERNED DATASET` (CT-5191).
 *
 * "Governed", not "Choose", because the adjective is load-bearing: these are
 * saved, reviewed queries, which is exactly why this path offers neither
 * filters nor an operation. The shape badge says the rest — a dataset already
 * declares whether it is grouped, a single value or a record set.
 *
 * Same anatomy as `EntityPickerCard`, down to the badge row: the two lists sit
 * in the same slot and swapping between them should not restyle the page.
 */
/** Case- and accent-insensitive, so "codigo" finds "código". */
const fold = (text) => String(text ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

function DatasetPicker({ value, onSelect }) {
  const { t } = useTranslation('dashboards');
  const { datasets, isLoading, error, refetch } = useDatasets();
  const [query, setQuery] = useState('');

  const byId = useMemo(
    () => Object.fromEntries(datasets.map((dataset) => [dataset.id, dataset])),
    [datasets],
  );

  /*
    The spec asked for the whole catalog with no "see more", and it was
    written when a workspace had four datasets. Dev has 349, and finding one
    meant scrolling all of them (CT-5886).

    Filtering client-side rather than through the API: `useDatasets` already
    pages the whole published catalog into memory, so a round trip per
    keystroke would buy nothing.
  */
  const visible = useMemo(() => {
    const needle = fold(query).trim();
    if (!needle) return datasets;
    return datasets.filter((dataset) => fold(dataset.name).includes(needle));
  }, [datasets, query]);

  // The shape and the display name travel with the selection rather than being
  // re-queried: the shape is what `dataShape` reads on this path, and the name
  // is what the live preview's meta line shows.
  const handleSelect = useCallback(
    (datasetId) => onSelect(datasetId, byId[datasetId]?.shape ?? '', byId[datasetId]?.name ?? ''),
    [byId, onSelect],
  );

  const body = () => {
    if (isLoading) return <PickerSkeleton count={4} />;
    if (error) {
      return (
        <PickerError
          title={t('widgetBuilder.data.datasets.errorTitle')}
          subtitle={t('widgetBuilder.data.datasets.errorSubtitle')}
          retryLabel={t('widgetBuilder.data.retry')}
          onRetry={refetch}
        />
      );
    }

    if (!visible.length) {
      return (
        <p className="m-0 px-1 py-6 text-center text-sm text-content-muted">
          {t('widgetBuilder.data.datasets.noMatches', { query })}
        </p>
      );
    }

    return (
      <PickerOptionGroup
        ariaLabel={t('widgetBuilder.data.datasets.title')}
        columns={2}
        className="grid gap-3 sm:grid-cols-2"
      >
        {visible.map((dataset) => {
          const shapeLabel = t(`widgetBuilder.data.datasets.shapes.${dataset.shape}`);
          const integrationLabel =
            t(integrationLabelKey(dataset.integration) ?? '') || dataset.integration;

          return (
            <DatasetPickerCard
              key={dataset.id}
              dataset={dataset}
              shapeLabel={shapeLabel}
              integrationLabel={integrationLabel}
              selected={value === dataset.id}
              onSelect={handleSelect}
            />
          );
        })}
      </PickerOptionGroup>
    );
  };

  return (
    <AccordionSection title={t('widgetBuilder.data.datasets.title')} titleSize="xs" defaultOpen>
      <div className="flex flex-col gap-3 pb-4 pt-3">
        {!isLoading && !error && datasets.length > 0 ? (
          <SearchInput
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onClear={() => setQuery('')}
            placeholder={t('widgetBuilder.data.datasets.searchPlaceholder')}
            aria-label={t('widgetBuilder.data.datasets.searchPlaceholder')}
          />
        ) : null}
        {body()}
      </div>
    </AccordionSection>
  );
}

DatasetPicker.propTypes = {
  value: PropTypes.string,
  /** `(datasetId, shape, label) => void` — both carried with the selection. */
  onSelect: PropTypes.func.isRequired,
};

export default DatasetPicker;
